import { motion } from 'framer-motion';
import { ArrowLeft, Check, Play, Download, Star } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

const packageData = {
  'credit-builder': {
    name: 'Credit Builder',
    price: '$197/mo',
    tagline: 'Foundation - Credit repair only',
    description: 'Perfect for those who need to fix their credit and build a strong foundation. Get unlimited disputes, AI-powered letters, and essential education.',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', // Replace with real demo video
    features: [
      {
        title: 'Unlimited Credit Disputes',
        description: 'Challenge every negative item on all 3 bureaus (Equifax, Experian, TransUnion). No limits.',
        demo: 'See the Disputes tab in action - select items, generate letters, track status.',
      },
      {
        title: 'AI Dispute Letter Factory',
        description: 'Generate FCRA-compliant dispute letters in seconds. Download PDF or send directly to bureaus.',
        demo: 'Watch how our AI creates personalized, legally-compliant letters automatically.',
      },
      {
        title: 'Real-Time Score Tracking',
        description: 'Monitor your credit scores from all 3 bureaus in real-time. See progress with visual charts.',
        demo: 'View your dashboard with live score updates and historical tracking.',
      },
      {
        title: 'Basic Credit Education',
        description: '6 essential lessons covering credit basics, utilization, disputes, and score optimization.',
        demo: 'Browse the education library with beginner-friendly modules.',
      },
      {
        title: 'Email Support',
        description: '48-hour response time for all questions and support needs.',
        demo: 'Submit questions through the support portal.',
      },
      {
        title: 'Monthly Progress Reports',
        description: 'Detailed monthly reports showing score changes, disputes filed, and next steps.',
        demo: 'See sample progress reports with actionable insights.',
      },
    ],
    demoScreenshots: [
      { title: 'Dashboard Overview', description: 'See your credit scores and progress at a glance' },
      { title: 'Dispute Management', description: 'Select and dispute negative items' },
      { title: 'Letter Factory', description: 'Generate professional dispute letters' },
      { title: 'Education Library', description: 'Learn credit repair strategies' },
    ],
  },
  'wealth-accelerator': {
    name: 'Wealth Accelerator',
    price: '$397/mo',
    tagline: 'Most Popular - Credit + Tax + Funding',
    description: 'The complete financial transformation package. Fix your credit, save thousands in taxes, and access business funding. This is where credit repair becomes wealth building.',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    features: [
      {
        title: 'Everything in Credit Builder',
        description: 'All credit repair features plus unlimited disputes and AI letter generation.',
        demo: 'Full access to credit repair dashboard.',
      },
      {
        title: 'AI Tax Deduction Scanner',
        description: 'Our AI analyzes your business and finds an average of $47,200 in deductions you\'re missing.',
        demo: 'See how the scanner identifies deductions with confidence scores.',
      },
      {
        title: 'Tax Strategy Optimizer',
        description: 'Get personalized tax strategies including Augusta Rule, S-Corp election, and entity optimization.',
        demo: 'View the tax strategy dashboard with savings calculations.',
      },
      {
        title: 'Entity Structure Consulting',
        description: 'Compare Sole Prop vs LLC vs S-Corp vs C-Corp. See exactly how much you\'ll save.',
        demo: 'Interactive entity comparison tool with real numbers.',
      },
      {
        title: 'Business Funding Matches',
        description: 'AI matches you with $50k-$150k in funding options based on your credit and business profile.',
        demo: 'See your funding readiness score and matched opportunities.',
      },
      {
        title: 'Goal Roadmap System',
        description: 'Step-by-step plans for buying a house, car, or securing business funding.',
        demo: 'View phase-by-phase roadmaps with task checklists.',
      },
      {
        title: 'Advanced Credit Education',
        description: '12 comprehensive modules covering advanced strategies, business credit, and wealth building.',
        demo: 'Access the full education library with expert tactics.',
      },
      {
        title: 'Priority Email Support',
        description: '24-hour response time for all questions.',
        demo: 'Priority support queue.',
      },
      {
        title: 'Bi-Weekly Strategy Calls',
        description: 'Group coaching calls every 2 weeks to discuss strategies and answer questions.',
        demo: 'Join live strategy sessions.',
      },
    ],
    demoScreenshots: [
      { title: 'Complete Dashboard', description: 'Credit + Tax + Funding in one place' },
      { title: 'Tax Deduction Scanner', description: 'AI finds $47k+ in savings' },
      { title: 'Funding Matches', description: 'Get matched with $50k-$150k' },
      { title: 'Goal Roadmaps', description: 'Step-by-step plans for major purchases' },
    ],
  },
  'business-empire': {
    name: 'Business Empire',
    price: '$697/mo',
    tagline: 'For Entrepreneurs - Full business suite',
    description: 'Everything you need to build a business empire. Advanced tax strategies, $100k-$250k funding access, business credit building, and complete education library.',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    features: [
      {
        title: 'Everything in Wealth Accelerator',
        description: 'Full credit repair, tax optimization, and funding access.',
        demo: 'Complete access to all previous features.',
      },
      {
        title: 'NAICS Code Optimizer',
        description: 'Choose the right NAICS code to maximize funding approval rates.',
        demo: 'Compare codes and see risk ratings.',
      },
      {
        title: 'Tier-1 Trade Line Access',
        description: 'Get access to vendors that report to business credit bureaus (Uline, Quill, Grainger).',
        demo: 'View trade line vendors and application links.',
      },
      {
        title: '0% Funding Strategy',
        description: 'Learn how to stack $100k-$250k in 0% APR business credit cards and lines of credit.',
        demo: 'See the 5-phase roadmap to interest-free capital.',
      },
      {
        title: 'Advanced Tax Strategies',
        description: 'Augusta Rule, S-Corp election, hiring your children, QBI deduction, and more.',
        demo: 'Detailed strategy guides with savings calculations.',
      },
      {
        title: 'Business Credit Building Roadmap',
        description: 'Step-by-step plan to build business credit separate from personal credit.',
        demo: 'Follow the roadmap to establish business credit.',
      },
      {
        title: 'Complete Credit Education Library',
        description: '20+ modules covering everything from basics to advanced wealth-building strategies.',
        demo: 'Full access to all education content.',
      },
      {
        title: 'Phone + Email Support',
        description: 'Call or email anytime with questions.',
        demo: 'Direct phone line access.',
      },
      {
        title: 'Weekly Strategy Calls',
        description: 'Weekly group coaching calls with Q&A.',
        demo: 'Join weekly sessions.',
      },
      {
        title: 'Quarterly Business Review',
        description: 'Comprehensive review of your credit, taxes, and funding every 3 months.',
        demo: 'See sample quarterly reports.',
      },
    ],
    demoScreenshots: [
      { title: 'Business Dashboard', description: 'Complete business financial command center' },
      { title: 'NAICS Optimizer', description: 'Choose the best code for funding' },
      { title: '0% Funding Strategy', description: 'Access $100k-$250k interest-free' },
      { title: 'Education Library', description: '20+ modules of expert knowledge' },
    ],
  },
  'millionaire-mentorship': {
    name: 'Millionaire Mentorship',
    price: '$1,497/mo',
    tagline: 'Elite - Personal coaching + full service',
    description: 'The ultimate financial transformation experience. Everything in Business Empire PLUS 1-on-1 coaching, custom strategies, dedicated account manager, and exclusive mastermind access.',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    features: [
      {
        title: 'Everything in Business Empire',
        description: 'Full access to all credit, tax, funding, and education features.',
        demo: 'Complete platform access.',
      },
      {
        title: '1-on-1 Coaching (4 Sessions/Month)',
        description: 'Private coaching calls with our financial experts. Get personalized strategies for your unique situation.',
        demo: 'Schedule your coaching sessions.',
      },
      {
        title: 'Custom Credit Strategies',
        description: 'Personalized credit repair plan based on your specific credit profile and goals.',
        demo: 'See your custom strategy document.',
      },
      {
        title: 'Custom Tax Planning with CPA Review',
        description: 'Work with our CPAs to create a custom tax plan that maximizes your savings.',
        demo: 'View sample custom tax plans.',
      },
      {
        title: 'Custom Funding Roadmap',
        description: 'Personalized funding strategy to access $250k+ in capital.',
        demo: 'See your custom funding plan.',
      },
      {
        title: 'Direct Phone/Text Access',
        description: 'Text or call your dedicated account manager anytime.',
        demo: 'Direct line to your team.',
      },
      {
        title: 'Dedicated Account Manager',
        description: 'One person who knows your entire financial situation and coordinates everything.',
        demo: 'Meet your account manager.',
      },
      {
        title: 'Same-Day Support',
        description: 'All questions answered within hours, not days.',
        demo: 'Priority support access.',
      },
      {
        title: 'Setup Fee Waived',
        description: 'Save $149 on setup fees.',
        demo: 'Immediate access.',
      },
      {
        title: 'Monthly Financial Review',
        description: 'Comprehensive monthly review of all your financial metrics and progress.',
        demo: 'See sample monthly reviews.',
      },
      {
        title: 'Exclusive Mastermind Group',
        description: 'Join our private community of high-achievers building wealth together.',
        demo: 'Access the mastermind portal.',
      },
    ],
    demoScreenshots: [
      { title: 'VIP Dashboard', description: 'Premium experience with white-glove service' },
      { title: 'Coaching Portal', description: 'Schedule and manage 1-on-1 sessions' },
      { title: 'Custom Strategies', description: 'Personalized plans for your situation' },
      { title: 'Mastermind Community', description: 'Network with other successful entrepreneurs' },
    ],
  },
};

export default function PackageDemo() {
  const params = useParams();
  const packageId = params['*']?.replace('demo/', '') || 'credit-builder';
  const pkg = packageData[packageId as keyof typeof packageData] || packageData['credit-builder'];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to={ROUTE_PATHS.PRICING}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">{pkg.name} Demo</h1>
              <p className="text-muted-foreground mt-1">{pkg.tagline}</p>
            </div>
            <div className="ml-auto">
              <Badge variant="outline" className="text-lg px-4 py-2 border-primary text-primary">
                {pkg.price}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Overview */}
        <Card className="p-8 mb-8 bg-gradient-to-br from-primary/5 to-accent/5">
          <p className="text-lg text-muted-foreground mb-6">{pkg.description}</p>
          <div className="flex gap-4 flex-wrap">
            <Button size="lg" asChild>
              <Link to={ROUTE_PATHS.CONTACT}>
                Get Started with {pkg.name}
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={ROUTE_PATHS.DASHBOARD}>
                View Live Dashboard
              </Link>
            </Button>
          </div>
        </Card>

        {/* Video Demo */}
        <Card className="p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Play className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold">Video Walkthrough</h2>
          </div>
          <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Play className="w-16 h-16 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">
                Video demo coming soon! For now, explore the live dashboard below.
              </p>
              <Button className="mt-4" asChild>
                <Link to={ROUTE_PATHS.DASHBOARD}>
                  Explore Live Dashboard
                </Link>
              </Button>
            </div>
          </div>
        </Card>

        {/* Features */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">What's Included</h2>
          <div className="space-y-4">
            {pkg.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ ...springPresets.gentle, delay: index * 0.05 }}
              >
                <Card className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{feature.description}</p>
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <Star className="w-4 h-4" />
                        <span className="font-medium">Demo: {feature.demo}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Screenshots */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">Dashboard Screenshots</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {pkg.demoScreenshots.map((screenshot, index) => (
              <Card key={index} className="p-6">
                <div className="aspect-video bg-muted rounded-lg mb-4 flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Download className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">Screenshot: {screenshot.title}</p>
                  </div>
                </div>
                <h3 className="font-semibold mb-1">{screenshot.title}</h3>
                <p className="text-sm text-muted-foreground">{screenshot.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Card className="p-8 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20 text-center">
          <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join hundreds of clients transforming their financial future with {pkg.name}.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" asChild>
              <Link to={ROUTE_PATHS.CONTACT}>
                Schedule Free Consultation
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={ROUTE_PATHS.PRICING}>
                Compare All Packages
              </Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
